import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import styles from '../pages/JobDashboard/JobDetailNew.module.css'; 
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader2 from '../components/loader2';
import showToast from "../components/ShowToast";
import Swal from 'sweetalert2';
import ModernNavbarPureCSS from "../components/Navbar/ModernNavBarPureCSS";

import {
  ArrowLeft,
  User,
  Camera,
  Search,
  MapPin,
  Home,
  LogOut,
  X,
  Briefcase
} from "lucide-react";

const HomeUserOnboardingNew = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  
  const { userId: userIdFromParams } = useParams(); 
  const [userId, setUserId] = useState(null);

  // --- State for all onboarding fields ---
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  // Image State
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageBase64, setProfileImageBase64] = useState(null);
  const [preview, setPreview] = useState(null); // This holds either the URL blob or the Base64 string
  const [imageError, setImageError] = useState(''); 

  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  
  // Mobile specific state
  const [mobile, setMobile] = useState('');
  const [mobileError, setMobileError] = useState('');

  const [addressResults, setAddressResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);

  const [selectedInterests, setSelectedInterests] = useState([]);
  const [interestSearchTerm, setInterestSearchTerm] = useState('');
  const [interestResults, setInterestResults] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // 1. Initial Token Decode
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.nameid);
      setUsername(decodedToken.UserName || 'User'); 
    }
  }, []);

  // 2. Fetch User Data Logic
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const decodedToken = jwtDecode(token);
      const loggedInUserId = decodedToken.nameid;
      const userType = decodedToken.UserType;

      if (userType !== 'Home') {
        showToast('error', 'You are not authorized to access this page.');
        navigate('/newhome');
        return;
      }

      setUserId(loggedInUserId);

      // SECURITY CHECK
      if (userIdFromParams && String(userIdFromParams) !== String(loggedInUserId)) {
        showToast('error', "You can only edit your own profile.");
        setIsReadOnly(true);
        return; 
      }
      
      setIsLoading(true);
      try {
        const profileResponse = await axios.get(`${API_BASE_URL}/api/HomeUserProfile/getUserProfileDataOnly?userId=${loggedInUserId}`);
        
        if (profileResponse.data && profileResponse.data.user) {
          const userData = profileResponse.data.user;
          
          setFirstName(userData.firstName || '');
          setLastName(userData.lastName || '');
          setMobile(userData.phone || '');
          setAddress(userData.addressComment || '');
          setPostcode(userData.postcode || '');
          
          // HANDLE IMAGE PREVIEW FROM API
          // If userData.profilePic is null, preview is null.
          // If userData.profilePic is "data:image...", preview is that string.
          setPreview(userData.profilePic); 
          
          if (userData.professions && userData.professions.length > 0) {
            const interests = userData.professions.map(p => ({
              id: p.professionId,
              name: p.name,
              type: 'Profession'
            }));
            setSelectedInterests(interests);
          }
        }
      } catch (err) {
        showToast('error', 'Failed to load your profile data.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userIdFromParams, navigate]);

  const handleLogout = () => {
      localStorage.removeItem('token');
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login');
  };

  const handleMobileChange = (e) => {
    const val = e.target.value;
    if (/^\d*$/.test(val)) {
        if (val.length <= 10) {
            setMobile(val);
            if (val.length > 0 && val.length < 10) {
                setMobileError('Mobile number must be 10 digits');
            } else {
                setMobileError('');
            }
        }
    }
  };

  const handleAddressSearchChange = async (e) => {
    const searchValue = e.target.value;
    setAddress(searchValue);
    if (searchValue.length > 2) {
      setIsSearchingAddress(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/GooglePlaces/addresscomplete`, { params: { input: searchValue } });
        setAddressResults(response.data?.predictions || []);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      } finally {
        setIsSearchingAddress(false);
      }
    } else {
      setAddressResults([]);
    }
  };

  const handleSelectAddress = async (suggestion) => {
    setAddress(suggestion.description);
    setAddressResults([]); 

    try {
      const response = await axios.get(`${API_BASE_URL}/api/GooglePlaces/placedetails`, { 
        params: { placeId: suggestion.place_id } 
      });
      
      if (response.data && response.data.result && response.data.result.address_components) {
        const addressComponents = response.data.result.address_components;
        const postcodeComponent = addressComponents.find(c => c.types.includes('postal_code'));
        if (postcodeComponent) {
          setPostcode(postcodeComponent.long_name);
        } else {
          setPostcode('');
        }
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
      const postcodeRegex = /\b\d{4}\b/g;
      const matches = suggestion.description.match(postcodeRegex);
      if (matches && matches.length > 0) {
        setPostcode(matches[matches.length - 1]);
      }
    }
  };

  const handleInterestSearch = async (searchTerm) => {
    setInterestSearchTerm(searchTerm);
    if (searchTerm.length > 0) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/Search/searchall?search=${searchTerm}`);
        setInterestResults(response.data.slice(0, 30));
      } catch (error) {
        console.error(`Error fetching interests:`, error);
      }
    } else {
      setInterestResults([]);
    }
  };

  const handleSelectInterest = (item) => {
    if (!selectedInterests.some((i) => i.id === item.id && i.type === item.type)) {
      setSelectedInterests([...selectedInterests, item]);
    }
    setInterestSearchTerm('');
    setInterestResults([]);
  };

  const handleRemoveInterest = (itemToRemove) => {
    setSelectedInterests(selectedInterests.filter((item) => !(item.id === itemToRemove.id && item.type === itemToRemove.type)));
  };

  // const handleImageUpload = (e) => {
  //   if (e.target.files[0]) {
  //     const file = e.target.files[0];
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setProfileImageBase64(reader.result);
  //     };
  //     reader.readAsDataURL(file);
  //     setPreview(URL.createObjectURL(file));
  //     setProfileImage(file);
  //     setImageError(''); // Clear error on successful select
  //   }
  // };

  const handleImageUpload = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];

      setProfileImage(file); // ✅ store file only
      setPreview(URL.createObjectURL(file));

      setImageError('');
    }
  };

  const handleSubmit = async () => {
    // 1. Mobile Validation
    if (mobile.length !== 10) {
        setMobileError('Mobile number must be exactly 10 digits');
        showToast('error', 'Please enter a valid 10-digit mobile number.');
        return;
    }

    // 2. Image Validation (Compulsory)
    // Works with API logic:
    // If API returned null, preview is null -> validation fails.
    // If API returned Base64, preview is string -> validation passes.
    // If User uploaded new, preview is blob URL -> validation passes.
    if (!preview) {
        setImageError('Profile image is required');
        showToast('error', 'Please upload a profile image to continue.');
        
        // Scroll to the image section
        const imageSection = document.getElementById('profile-image-card');
        if(imageSection) imageSection.scrollIntoView({ behavior: 'smooth' });
        
        return;
    }

    setIsLoading(true);

    if (!firstName || !lastName || !address || !mobile) {
        showToast('error', 'Please fill in all required fields.');
        setIsLoading(false);
        return;
    }

    try {
        // Step 1: Update User Details
        const userDetailsPayload = {
            user_id: userId,
            phone: mobile,
            firstName: firstName,
            lastName: lastName,
            postcode: postcode,
            addressComment: address
        };
        await axios.post(`${API_BASE_URL}/api/HomeOnboarding/update-user-details`, userDetailsPayload);
        showToast('success', 'Personal details saved!');

        // Step 2: Update Profile Picture
        // Only if profileImageBase64 is set (meaning user uploaded a NEW one).
        // If user kept the API loaded image, profileImageBase64 is null, so we skip this (saving bandwidth/processing).
        // if (profileImageBase64) {
        //     const profilePicPayload = {
        //         userId: userId,
        //         imageBase64: profileImageBase64
        //     };
        //     await axios.put(`${API_BASE_URL}/api/HomeOnboarding/update-profile-pic`, profilePicPayload);
      // }
      
      if (profileImage) {
        const formData = new FormData();

        formData.append("userId", userId);
        formData.append("profileImage", profileImage); // key must match backend

        await axios.put(`${API_BASE_URL}/api/HomeOnboarding/update-profile-pic`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
      }

        // Step 3: Save Search Selections
        const professionIds = selectedInterests.filter(i => i.type === 'Profession').map(i => i.id);
        const serviceIds = selectedInterests.filter(i => i.type === 'Service').map(i => i.id);
        const supplierTypeIds = selectedInterests.filter(i => i.type === 'SupplierType').map(i => i.id);
        const ancillaryIds = selectedInterests.filter(i => i.type === 'ProfessionAncillary').map(i => i.id);

        const selectionsPayload = {
            userId,
            professionIds,
            serviceIds,
            supplierTypeIds,
            ancillaryIds
        };

        if (professionIds.length > 0 || serviceIds.length > 0 || supplierTypeIds.length > 0 || ancillaryIds.length > 0) {
            await axios.post(`${API_BASE_URL}/api/HomeOnboarding/saveSearchSelections`, selectionsPayload);
        }

        try {
            await axios.post(`${API_BASE_URL}/api/HomeOnboarding/setOnboardingComplete?userId=${userId}`);
        } catch (error) {
            console.error("Onboarding API error:", error);
        }

        Swal.fire({
            title: "Success!",
            text: `Your profile has been ${userIdFromParams ? 'updated' : 'created'} successfully!`,
            icon: "success",
            confirmButtonText: "Go to Dashboard",
        }).then(() => {
            if (!userIdFromParams) {
                localStorage.setItem('shouldOnboard', "0");
            }
            navigate('/newhome');
        });

    } catch (err) {
        showToast('error', 'An error occurred while saving your profile.');
        console.error("Error submitting form:", err.response ? err.response.data : err);
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <div className={`${styles.jobDetailNew_minHeightScreen} ${styles.jobDetailNew_bgGradientPage}`}>

      <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'profile'} />

      <header className={styles.jobDetailNew_header}>
        <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_headerContent}`}>
          <div className={styles.jobDetailNew_headerLeft}>
            {userIdFromParams && (
              <button onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{ color: 'Navy' }}>
                <ArrowLeft className={styles.jobDetailNew_iconMd} />
              </button>
            )}
            <div className={styles.jobDetailNew_headerLogo}>
              <Home className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber400}`} />
              <span className={styles.jobDetailNew_headerTitle}>Build Start</span>
            </div>
            <div className={styles.jobDetailNew_headerSeparator}></div>
            <div>
              <h1 className={styles.jobDetailNew_headerTitle}>{userIdFromParams ? "Edit Your Profile" : "Welcome! Let's Get Started"}</h1>
              <p className={styles.jobDetailNew_textBlue900}>{userIdFromParams ? "Update your personal details" : "Complete your profile to find the best services"}</p>
            </div>
          </div>
          <div className={styles.jobDetailNew_headerRight}>
            <button onClick={handleLogout} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{ color: 'Navy' }}>
              <LogOut className={styles.jobDetailNew_iconMd} />
              <span className={styles.jobDetailNew_ml2}>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`}>
        {isLoading && <Loader2 />}
        <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_lg_gridCols3}`}>
          <div className={`${styles.jobDetailNew_lg_colSpan2} ${styles.jobDetailNew_spaceY6}`}>
            
            {/* --- Personal Details Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <User className={styles.jobDetailNew_iconMd} />
                  <span>Personal Details <span className="text-red-500">*</span></span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="firstName" className={styles.jobDetailNew_label}>First Name</label>
                    <input id="firstName" type="text" placeholder="Your first name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                  <div>
                    <label htmlFor="lastName" className={styles.jobDetailNew_label}>Last Name</label>
                    <input id="lastName" type="text" placeholder="Your last name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                </div>
              </div>
            </div>

            {/* --- Contact Information Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <MapPin className={styles.jobDetailNew_iconMd} />
                  <span>Contact Information <span className="text-red-500">*</span></span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                 <div>
                    <label htmlFor="mobile" className={styles.jobDetailNew_label}>Mobile</label>
                    <input 
                      id="mobile" 
                      type="tel" 
                      placeholder="04xxxxxxxx" 
                      value={mobile} 
                      onChange={handleMobileChange} 
                      className={`${styles.jobDetailNew_input} ${mobileError ? 'border-red-500' : ''}`}
                      disabled={isReadOnly} 
                    />
                    {mobileError && <p className="text-red-500 text-sm mt-1">{mobileError}</p>}
                  </div>
                  <div>
                    <label htmlFor="address" className={styles.jobDetailNew_label}>Your Address</label>
                    <div className={styles.jobDetailNew_inputWithIconContainer}>
                      <Search className={styles.jobDetailNew_inputIcon} />
                      <input id="address" type="text" placeholder="Search for your address" value={address} onChange={handleAddressSearchChange} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} disabled={isReadOnly} />
                    </div>
                    {addressResults.length > 0 && (
                        <ul className={styles.jobDetailNew_dropdownResults}>
                          {addressResults.map((suggestion) => (
                            <li key={suggestion.place_id} className={styles.jobDetailNew_resultItem} onClick={() => handleSelectAddress(suggestion)}>
                              {suggestion.description}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
              </div>
            </div>

            {/* --- Interests Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Briefcase className={styles.jobDetailNew_iconMd} />
                  <span>What are you looking for?</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                  <label className={styles.jobDetailNew_label}>Search for professions or services you're interested in</label>
                  <div className={styles.jobDetailNew_inputWithIconContainer}>
                    <Search className={styles.jobDetailNew_inputIcon} />
                    <input
                      type="text"
                      placeholder="e.g., Plumber, Electrician, Landscaping..."
                      className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`}
                      value={interestSearchTerm}
                      onChange={(e) => {
                        setInterestSearchTerm(e.target.value);
                        handleInterestSearch(e.target.value);
                      }}
                      disabled={isReadOnly}
                    />
                     {interestResults.length > 0 && (
                      <ul className={styles.jobDetailNew_dropdownResults}>
                        {interestResults.map((item) => (
                          <li key={`${item.id}-${item.type}`} className={styles.jobDetailNew_resultItem} onClick={() => handleSelectInterest(item)}>
                            {item.name} ({item.type})
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className={styles.jobDetailNew_flexWrap} style={{ marginTop: '1rem' }}>
                    {selectedInterests.map((interest) => (
                      <span key={`${interest.id}-${interest.type}`} className={styles.jobDetailNew_badge}>
                        {interest.name} ({interest.type})
                        {!isReadOnly && (
                          <button type="button" onClick={() => handleRemoveInterest(interest)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}>
                            <X className={styles.jobDetailNew_buttonIcon} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* --- Profile Image Card --- */}
            <div id="profile-image-card" className={`${styles.jobDetailNew_card} ${imageError ? 'border-red-500' : ''}`}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Camera className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber600}`} />
                  <span>Profile Image <span className="text-red-500">*</span></span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`}>Upload a profile picture (Required).</p>
                <div className={styles.jobDetailNew_uploadArea}>
                  <input id="profile-image-upload" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={isReadOnly} />
                  <label htmlFor="profile-image-upload" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`} style={{cursor: isReadOnly ? 'default' : 'pointer'}}>
                    Browse File
                  </label>
                </div>
                
                {/* Validation Error Message */}
                {imageError && (
                  <p className="text-red-500 text-sm font-semibold">{imageError}</p>
                )}

                {preview && (
                  <div className={styles.jobDetailNew_uploadedImagesGrid}>
                    <div className={styles.jobDetailNew_uploadedImageItem}>
                      <img src={preview} alt="Profile Preview" className={styles.jobDetailNew_uploadedImage} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.jobDetailNew_spaceY6}>
            <div className={`${styles.jobDetailNew_card} ${styles.jobDetailNew_sidebarCard}`}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_sidebarHeader}`}>
                <h3 className={styles.jobDetailNew_sidebarTitle}>Profile Summary</h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_sidebarContentSpaceY4}`}>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>NAME</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{firstName} {lastName || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>LOCATION</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{address || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>INTERESTS</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{selectedInterests.length} selected</p>
                  </div>
                </div>
                <div className={styles.jobDetailNew_separator}></div>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  {!isReadOnly && (
                    <button type="button" onClick={handleSubmit} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonPrimary}`}>
                      <User className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      {userIdFromParams ? "Save Changes" : "Complete Onboarding"}
                    </button>
                  )}
                  {userIdFromParams && (
                    <button type="button" onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonOutline}`}>
                      <ArrowLeft className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      Go Back
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeUserOnboardingNew;